import { API } from 'consts';

const KEY = 'nextUrl';
const MAX_AGE_MS = 60 * 60 * 1000;
// The pages a person reaches before sign-in. A `next` on any other page is not stored: on a
// sign-in link, whoever sent the link would choose where the Host lands, and on a Store link
// it would wait in the browser for a later sign-in.
const STORING_PATH_PATTERN = /^\/(login|signup)(\/|$)/i;
// The approval page for a Connected Application. It is on the API host, and it is the one path
// the backend sends a signed-out person to the login page for (LOGIN_URL).
const APPROVAL_PATH = '/o/authorize/';

export type NextDestination = { kind: 'app'; path: string } | { kind: 'approval'; url: string };

const isPathOnThisSite = (value: string) => {
  if (!value.startsWith('/')) {
    return false;
  }

  try {
    // Parsed like the browser does it, so "//host", "/\\host" and a tab before the second
    // slash all count as another site.
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

// The backend writes the whole URL of the approval page into `next`, because the login page is
// on another host than the API. A link written by hand has the path only. Both become the path.
// The path is compared after the URL is resolved, so "/o/authorize/../x" is not the approval page.
const toApprovalPath = (next: string) => {
  try {
    const url = new URL(next, API.root);
    const isOnApi = url.origin === new URL(API.root).origin;

    return isOnApi && url.pathname === APPROVAL_PATH ? url.pathname + url.search : null;
  } catch {
    return null;
  }
};

const readNext = (search: string) => {
  const params = Array.from(new URLSearchParams(search));
  const index = params.findIndex(([key]) => key === 'next');
  if (index === -1) {
    return null;
  }

  const approvalPath = toApprovalPath(params[index][1]);
  if (!approvalPath) {
    return params[index][1];
  }

  // A link written by hand can leave the query of the approval page unencoded, so its
  // parameters land after `next` and not inside it.
  const rest = params
    .slice(index + 1)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  return rest ? `${approvalPath}${approvalPath.includes('?') ? '&' : '?'}${rest}` : approvalPath;
};

export const storeNextUrl = ({ pathname, search }: { pathname: string; search: string }) => {
  const next = STORING_PATH_PATTERN.test(pathname) ? readNext(search) : null;

  if (next) {
    localStorage.setItem(KEY, JSON.stringify({ next, storedAt: Date.now() }));
  }
};

export const clearNextUrl = () => localStorage.removeItem(KEY);

const takeNextUrl = () => {
  try {
    const { next, storedAt } = JSON.parse(localStorage.getItem(KEY) ?? 'null') ?? {};
    const isFresh = Date.now() - storedAt < MAX_AGE_MS;

    return typeof next === 'string' && isFresh ? next : null;
  } catch {
    return null;
  } finally {
    clearNextUrl();
  }
};

// `search` is the query of the page that the person signs in on. Its `next` wins over the
// stored copy, which has a time limit and is not there when the page was reached by a click.
export const takeNextDestination = (search = ''): NextDestination | null => {
  const stored = takeNextUrl();
  const next = readNext(search) ?? stored;
  if (!next) {
    return null;
  }

  const approvalPath = toApprovalPath(next);
  if (approvalPath) {
    return { kind: 'approval', url: new URL(approvalPath, API.root).href };
  }

  return isPathOnThisSite(next) ? { kind: 'app', path: next } : null;
};

// Reads the query only and leaves the stored copy, which a sign-in link still needs.
export const hasApprovalNext = (search: string) => {
  const next = readNext(search);

  return next !== null && toApprovalPath(next) !== null;
};

// False when there is nowhere to go, so the caller can pick its own page.
export const goToNextDestination = (
  history: { push: (path: string) => void },
  next: NextDestination | null,
) => {
  if (next?.kind === 'approval') {
    window.location.href = next.url;
  } else if (next?.kind === 'app') {
    history.push(next.path);
  }

  return Boolean(next);
};
